<template>
  <div class="home">
    <div class="home__load-images">
      <img
        v-for="(data, i) in currentPage"
        :key="`photo-${i}`"
        :src="data.src"
        :alt="data.alt"
      >
    </div>
    <transition
      @enter="enter"
      @leave="leave"
      mode="out-in"
      :appear="true"
    >
      <div class="slide__container" :key="currentIndex">
        <AppFigure
          v-for="number in [currentIndex]"
          :key="number"
          :isLazy="false"
          :data="{
            src: currentData.src,
            alt: currentData.alt,
          }"
        />
        <div class="slide__content">
          <SliderButton
            orientation="left"
            @click.native="prev()"
          />
          <div>
            <router-link
              v-if="currentData.url !== undefined"
              :to="currentData.url"
            >
              <p class="slide__eyelet">
                0{{ (Math.abs(this.currentIndex) % this.currentPage.length) + 1 }}
                  / 0{{ currentPage.length }} {{ currentData.eyelet }}
              </p>
              <h2
                class="slide__title"
                v-html="currentData.title"
                :data-lang="$store.state.currentLang"
              ></h2>
              <span class="slide__line"></span>
              <p class="slide__subtitle">{{ currentData.subtitle }}</p>
            </router-link>
          </div>
          <SliderButton
            orientation="right"
            @click.native="next()"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { TimelineLite } from 'gsap/all';
import title from '@/scripts/animations/title';

import AppFigure from '@/components/AppFigure.vue';
import SliderButton from '@/components/SliderButton.vue';

export default {
  name: 'home',

  components: {
    AppFigure,
    SliderButton,
  },

  data() {
    return {
      currentIndex: 0,
      isAnimated: false,
    };
  },

  computed: {
    ...mapState(['currentPage']),

    currentData() {
      return this.currentPage[Math.abs(this.currentIndex) % this.currentPage.length];
    },
  },

  mounted() {
    const burger = document.querySelector('.burger');
    const menu = document.querySelector('[data-mobileMenu]');
    if (burger.classList.contains('close')) {
      burger.classList.remove('close');
      menu.setAttribute('data-mobileMenu', 'hidden');
    }
  },

  methods: {
    next() {
      if (this.isAnimated) return;

      this.isAnimated = true;

      this.currentIndex += 1;
      this.direction = 'next';
    },

    prev() {
      if (this.isAnimated) return;
      this.isAnimated = true;

      this.currentIndex -= 1;
      this.direction = 'prev';
    },

    enter(el, done) {
      const figure = el.querySelector('.picture__div-img');
      const titleEl = el.querySelector('.slide__title');
      const eyelet = el.querySelector('.slide__eyelet');
      const subtitle = el.querySelector('.slide__subtitle');
      const line = el.querySelector('.slide__line');
      const tl = new TimelineLite({
        onComplete: () => {
          this.isAnimated = false;
          done();
        },
      });

      tl.addLabel('start')
        .from(figure, 1, {
          autoAlpha: 0,
        })
        .from(line, 1, {
          scaleX: 0,
          transformOrigin: '0% 50%',
        }, 'start')
        .add(title(titleEl), 'start')
        .from(eyelet, 0.4, {
          autoAlpha: 0,
        }, 'start+=0.2')
        .from(subtitle, 0.4, {
          autoAlpha: 0,
        }, 'start+=0.2');
    },
    leave(el, done) {
      const figure = el.querySelector('.picture__div-img');
      const titleEl = el.querySelector('.slide__title');
      const eyelet = el.querySelector('.slide__eyelet');
      const subtitle = el.querySelector('.slide__subtitle');
      const line = el.querySelector('.slide__line');

      const tl = new TimelineLite({
        onComplete: () => {
          done();
        },
      });

      tl.addLabel('start')
        .to(figure, 1, {
          autoAlpha: 0,
        }, 'start')
        .to(line, 1, {
          scaleX: 0,
          transformOrigin: '0% 50%',
        }, 'start')
        .to(titleEl, 0.8, {
          y: -20,
          autoAlpha: 0,
        }, 'start+=0.2')
        .to(eyelet, 0.4, {
          autoAlpha: 0,
        }, 'start+=0.2')
        .to(subtitle, 0.4, {
          autoAlpha: 0,
        }, 'start+=0.2');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~sass-mq/mq";
@import '../scss/utils';
@import "../scss/variables";
@import "../scss/functions";

.home {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;

  &__load-images {
    @include visually-hidden;
  }

  .slide__container,
  figure {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .slide__container {
    background-color: #2d2b29;
  }

  .slide__content {
    position: absolute;
    bottom: 140px;
    left: 20px;
    width: calc(100% - 40px);

    display: flex;
    align-items: flex-end;
    justify-content: space-between;

    @include mq(ipadP) {
      bottom: 80px;
      left: 40px;
      width: calc(100% - 80px);
    }

    @include mq(ipadL) {
      bottom: 80px;
      left: 140px;
      width: calc(100% - 180px);
    }

    & button {
      flex: 0 0 auto;

      &:first-of-type {
        display: none;

        @include mq(ipadP) {
          display: block;
        }
      }

      &:last-of-type {
        margin-left: 20px;

        @include mq(ipadP) {
          margin-left: 0;
        }
      }
    }

    & div {
      flex: 1 0 auto;

      @include mq(ipadP) {
        margin: 0 40px;
      }
    }

    .slide__eyelet {
      text-transform: uppercase;
      font-size: font-size(s);
      font-weight: bold;
      letter-spacing: 2px;
    }

    .slide__title {
      color: white;
      font-size: font-size(xl);
      margin: 20px 0;

      &::after {
        position: absolute;
        bottom: 0px;
        right: 0;

        font-size: font-size(s);
        text-transform: uppercase;
        font-weight: bold;

        opacity: 0.3;
        transition: opacity 0.3s ease-in-out;
      }

      &[data-lang="it"]::after {
        content: '+';

        @include mq(ipadP) {
          content: 'scopri di più';
        }
      }

      &[data-lang="en"]::after {
        content: '+';

        @include mq(ipadP) {
          content: 'discover more';
        }
      }

      @include mq(ipadP) {
        font-size: font-size(xxl);
      }

      @include mq($and: $is-not-touch-device) {
        &::after {
          opacity: 0;
        }
        &:hover::after {
          opacity: 0.7;
        }
      }
    }

    .slide__subtitle {
      transform: translateY(20px);
    }
  }

  .slide__line {
    display: inline-block;
    width: 100%;
    height: 1px;
    background-color: $color-white;
  }

  figure {
    &::after {
      content: '';

      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      background-color: rgba(rgb(37, 31, 26), 0.7);
    }
  }
}
</style>
